import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sparkles, Trash2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CREDENTIAL_OPTIONS = ['PA-C', 'RNFA', 'MD', 'DO', 'NP'] as const;

interface AssistantResolutionPopoverProps {
  value: string;
  onResolve: (value: string) => void;
  onRemoveAssistant: () => void;
  highlighted?: boolean;
}

export function AssistantResolutionPopover({
  value,
  onResolve,
  onRemoveAssistant,
  highlighted,
}: AssistantResolutionPopoverProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState('');
  const [saveToTemplate, setSaveToTemplate] = useState(false);

  const isResolved = value.trim().length > 0;

  const handleSave = () => {
    if (!name.trim() || !credentials) {
      toast.error('Please enter assistant name and credentials.');
      return;
    }
    const fullValue = `${name.trim()}, ${credentials}`;
    onResolve(fullValue);

    if (saveToTemplate) {
      toast.success('✓ Preferences Saved: Default Assistant Updated.', {
        description: `${fullValue} saved as default in your Master Template.`,
      });
    }

    setOpen(false);
    setSaveToTemplate(false);
  };

  const handleUndo = () => {
    onResolve('');
    setName('');
    setCredentials('');
  };

  if (isResolved) {
    return (
      <span
        data-compliance-gap="assistant_verification"
        className="inline-flex items-center gap-1 align-baseline mx-0.5"
      >
        <button
          onClick={handleUndo}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer border rounded-md px-2 sm:px-2.5 py-1 bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200 print:bg-transparent print:text-black print:border-none print:p-0 print:font-normal print:inline print:shadow-none"
        >
          ✓ Assistant: {value}
          <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-emerald-500 print:hidden" />
        </button>

        {/* Trash can */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onRemoveAssistant}
                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors print:hidden"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Remove Assistant from Case</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span className="print:hidden">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center justify-center w-4 h-4 cursor-help">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px]">
                <p className="text-xs font-medium">Surgeon Assist</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Modifier -80 will be applied. Incorrect assistant documentation is a common cause of claim denials.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      </span>
    );
  }

  return (
    <span
      data-compliance-gap="assistant_verification"
      className={cn(
        "inline-flex items-center gap-1 align-baseline mx-0.5 transition-all duration-500",
        highlighted && "rounded-lg ring-2 ring-amber-400 ring-offset-2 ring-offset-background bg-amber-50/60 px-1 py-0.5"
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer border rounded-md px-2 sm:px-2.5 py-1 bg-muted text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground animate-pulse print:hidden"
          >
            <UserCheck className="w-3.5 h-3.5" />
            Verify: Assistant Utilized
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-muted-foreground/40" />
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-80 p-0" sideOffset={6}>
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-primary" />
              Assistant Surgeon Details
            </h4>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Assistant Name</label>
              <Input
                placeholder="e.g., Sarah Jenkins"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-9"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Credentials</label>
              <Select value={credentials} onValueChange={setCredentials}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select credentials" />
                </SelectTrigger>
                <SelectContent>
                  {CREDENTIAL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleSave}
              disabled={!name.trim() || !credentials}
              className="w-full h-9"
              size="sm"
            >
              Save & Apply
            </Button>

            <Separator />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="save-assistant-template"
                  className="text-[11px] text-muted-foreground cursor-pointer select-none leading-tight"
                >
                  Save as default assistant in my Master Template
                </label>
                <Switch
                  id="save-assistant-template"
                  checked={saveToTemplate}
                  onCheckedChange={setSaveToTemplate}
                  className="scale-75 shrink-0"
                />
              </div>
              <p className="text-[10px] text-muted-foreground/70 leading-snug flex items-start gap-1">
                <Sparkles className="w-3 h-3 text-amber-500 shrink-0 mt-px" />
                <span>Enabling this will pre-fill this assistant on all future cases.</span>
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Trash can — available even when unresolved to skip entirely */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onRemoveAssistant}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors print:hidden"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">Remove Assistant from Case</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <span className="print:hidden">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center justify-center w-4 h-4 cursor-help">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[280px]">
              <p className="text-xs font-medium">Surgeon Assist</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Modifier -80 is required if an assistant surgeon was used. Click the trash icon to remove if no assistant was utilized.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    </span>
  );
}
